import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Progress } from "../../../components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";
import { Label } from "../../../components/ui/label";
import { UploadCloud, FileWarning, Check, Loader2 } from "lucide-react";
import { usePresentationsList } from "../../../lib/presentationQueries";
import { useSessionsOverview } from "../../../lib/sessionQueries";
import { uploadFile } from "../../../lib/fileQueries";
import { useCreateImportPreview, useCancelImport } from "../../../lib/workDocumentImportQueries";
import AudienceGate from "./AudienceGate";
import { AUDIENCE_OPTION_LABELS } from "./AudienceGate";

const LAYER_OPTIONS = [
  { value: "structure", label: "Structure" },
  { value: "vocal_delivery", label: "Vocal Delivery" },
  { value: "body_language", label: "Body Language" },
  { value: "slide_design", label: "Slide Design" },
  { value: "storytelling", label: "Storytelling" },
  { value: "qa_handling", label: "Q&A Handling" },
  { value: "executive_presence", label: "Executive Presence" },
];

const IMPORT_STEPS = [
  { key: "uploading", label: "Uploading original file" },
  { key: "reading", label: "Reading interactive markers" },
  { key: "validating", label: "Validating document" },
  { key: "preparing", label: "Preparing preview" },
];

/**
 * Master v5 Prompt 11 — the single reusable upload dialog.
 *
 * - `context` supplied (contextual upload, e.g. from a Presentation's
 *   Files area): the exact target is PREFILLED and shown for confirmation
 *   only — no classification picker, no AudienceGate, no "Process as"
 *   (interactive-blocks import is only offered from the general Files
 *   workbench flow, matching Prompt 30C's approved scope exactly).
 * - `context` omitted (a General Files-page upload): the user MUST
 *   answer "What is this file related to?" with one valid, real target
 *   before the Upload button is enabled. In a team workspace, a fresh
 *   unselected AudienceGate ALSO gates the upload.
 *
 * Master v5 Prompt 30C Phase 2 — adds "Process as: Static file /
 * Interactive blocks" (default Static file). Choosing Interactive
 * blocks changes NOTHING about the upload itself — it goes through the
 * exact same upload-init/storage-upload/Bunny pipeline as any other
 * file — it only adds ONE extra step afterward: calling the existing
 * Phase 1 import-preview endpoint on the just-uploaded FileAsset. A
 * Static file never touches the parser.
 */
export default function FileUploadDialog({
  open, onOpenChange, workspaceId, actorRole, isTeamWorkspace, teamMembers,
  context, onUploaded, onImportReady,
}) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState(context ? context.targetType : null);
  const [subTarget, setSubTarget] = useState(context ? context.targetId : null);
  const [audienceSelection, setAudienceSelection] = useState(null);
  const [processAs, setProcessAs] = useState("static");
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [importStage, setImportStage] = useState(null); // null while not importing; otherwise one of IMPORT_STEPS[].key, or "failed"
  const [importFailure, setImportFailure] = useState(null); // { fasId, message }
  const [uploadedFasId, setUploadedFasId] = useState(null);

  const isGeneral = !context;
  // DOCX-to-blocks import is coach-only (the backend's import-preview
  // endpoint rejects client_member actors), so the "Process as" choice
  // is only ever offered to a coach — a client always uploads Static.
  const canImport = isGeneral && actorRole === "coach";
  const presentations = usePresentationsList(isGeneral ? workspaceId : null);
  const sessionsOverview = useSessionsOverview(isGeneral ? workspaceId : null);
  const bookedSessions = sessionsOverview.data?.booked || [];
  const createImportPreview = useCreateImportPreview(workspaceId);
  const cancelImport = useCancelImport(workspaceId);

  const resolvedTarget = context || (category ? { targetType: category, targetId: subTarget } : null);
  const classificationComplete = !!(resolvedTarget && resolvedTarget.targetType && resolvedTarget.targetId);
  const needsAudienceGate = isGeneral && isTeamWorkspace;
  const isInteractive = canImport && processAs === "interactive";
  const fileIsDocx = !file || file.name.toLowerCase().endsWith(".docx");
  const canSubmit = !!file && classificationComplete && (!needsAudienceGate || audienceSelection) && progress === null && importStage === null
    && (!isInteractive || fileIsDocx);

  function reset() {
    setFile(null); setProgress(null); setError(null); setImportStage(null); setImportFailure(null); setUploadedFasId(null);
    if (isGeneral) { setCategory(null); setSubTarget(null); setAudienceSelection(null); setProcessAs("static"); }
  }

  function handleFileChange(picked) {
    setError(null);
    if (isInteractive && picked && !picked.name.toLowerCase().endsWith(".docx")) {
      setError("Interactive blocks currently support DOCX files only. Upload this file as a Static file instead.");
      setFile(null);
      return;
    }
    setFile(picked);
  }

  async function runImportPreview(fasId) {
    setImportStage("reading");
    try {
      // A single backend call performs marker-reading, validation and
      // preview-preparation together — these three steps are shown in
      // sequence for a clear, honest sense of progress on what is
      // genuinely happening server-side, not a fabricated delay.
      const previewPromise = createImportPreview.mutateAsync(fasId);
      await new Promise((res) => setTimeout(res, 350));
      setImportStage("validating");
      const draft = await previewPromise;
      await new Promise((res) => setTimeout(res, 250));
      setImportStage("preparing");
      await new Promise((res) => setTimeout(res, 250));

      if (draft.status === "invalid") {
        setImportStage("failed");
        setImportFailure({ fasId, message: draft.errors?.[0]?.message || "This document couldn't be read as an interactive workbook.", draft });
        return;
      }
      setImportStage("done");
      onImportReady && onImportReady({ workspaceId, fasId, importId: draft.import_id, draft });
      reset();
      onOpenChange(false);
    } catch (e) {
      setImportStage("failed");
      setImportFailure({ fasId, message: e.body?.detail?.message || e.body?.detail || e.message || "Import failed" });
    }
  }

  async function handleUpload() {
    if (!canSubmit) return;
    setError(null);
    setProgress(0);
    try {
      const result = await uploadFile({
        workspaceId, file, onProgress: setProgress,
        payload: {
          target_type: resolvedTarget.targetType, target_id: resolvedTarget.targetId, is_primary: true,
          is_general_classification: isGeneral,
          ...(needsAudienceGate ? { audience_selection: audienceSelection } : {}),
        },
      });
      const fasId = result.asset.fas_id;
      setUploadedFasId(fasId);
      if (isInteractive) {
        setProgress(null);
        setImportStage("uploading"); // shown briefly as "complete" before advancing
        await runImportPreview(fasId);
      } else {
        onUploaded && onUploaded(result);
        reset();
        onOpenChange(false);
      }
    } catch (e) {
      setError(e.body?.detail || e.message || "Upload failed");
      setProgress(null);
      setImportStage(null);
    }
  }

  async function handleCancelFailedImport() {
    if (importFailure?.draft?.import_id) {
      try { await cancelImport.mutateAsync(importFailure.draft.import_id); } catch { /* best-effort — the draft's own TTL cleans it up regardless */ }
    }
    reset();
    onOpenChange(false);
  }

  function handleUploadFailedAsStatic() {
    // The file is ALREADY a completely normal static FileAsset — parsing
    // it as interactive blocks was an ADDITIONAL step layered on top, so
    // "keep it as Static" requires no further upload of any kind.
    onUploaded && onUploaded({ asset: { fas_id: importFailure.fasId } });
    reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="file-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-ink">Upload a file</DialogTitle>
          <DialogDescription>
            {context ? `Attach to: ${context.label}` : "What is this file related to?"}
          </DialogDescription>
        </DialogHeader>

        {isGeneral && (
          <div className="space-y-2" data-testid="file-upload-classification">
            <Select value={category || ""} onValueChange={(v) => { setCategory(v); setSubTarget(null); }}>
              <SelectTrigger data-testid="file-upload-classification-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="layer">Practice layer</SelectItem>
                <SelectItem value="presentation">Presentation</SelectItem>
                <SelectItem value="session">Session</SelectItem>
              </SelectContent>
            </Select>
            {category === "layer" && (
              <Select value={subTarget || ""} onValueChange={setSubTarget}>
                <SelectTrigger data-testid="file-upload-classification-sub"><SelectValue placeholder="Which layer?" /></SelectTrigger>
                <SelectContent>{LAYER_OPTIONS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {category === "presentation" && (
              <Select value={subTarget || ""} onValueChange={setSubTarget}>
                <SelectTrigger data-testid="file-upload-classification-sub"><SelectValue placeholder="Which presentation?" /></SelectTrigger>
                <SelectContent>{(presentations.data?.items || []).map((p) => <SelectItem key={p.pres_id} value={p.pres_id}>{p.title}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {category === "session" && (
              <Select value={subTarget || ""} onValueChange={setSubTarget}>
                <SelectTrigger data-testid="file-upload-classification-sub"><SelectValue placeholder="Which session?" /></SelectTrigger>
                <SelectContent>{bookedSessions.map((s) => <SelectItem key={s.ses_id} value={s.ses_id}>{s.title || s.ses_id}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        )}

        {needsAudienceGate && classificationComplete && !audienceSelection && (
          <AudienceGate actorRole={actorRole} teamMembers={teamMembers} onConfirm={setAudienceSelection} />
        )}
        {needsAudienceGate && audienceSelection && (
          <p className="text-xs text-ink-soft" data-testid="file-upload-audience-confirmed">
            Audience: {AUDIENCE_OPTION_LABELS[audienceSelection.choice] || audienceSelection.choice}
            <button className="ml-2 underline" onClick={() => setAudienceSelection(null)} data-testid="file-upload-audience-change">change</button>
          </p>
        )}

        {isGeneral && importStage === null && canImport && (
          <div className="space-y-2" data-testid="file-upload-process-as">
            <Label className="text-xs uppercase tracking-wide text-ink-soft">Process as</Label>
            <RadioGroup value={processAs} onValueChange={(v) => { setProcessAs(v); setError(null); }} className="flex gap-4">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="static" id="process-as-static" data-testid="file-upload-process-as-static" />
                <Label htmlFor="process-as-static" className="text-sm cursor-pointer">Static file</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="interactive" id="process-as-interactive" data-testid="file-upload-process-as-interactive" />
                <Label htmlFor="process-as-interactive" className="text-sm cursor-pointer">Interactive blocks</Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {importStage === null && (
          <label
            className="flex flex-col items-center gap-2 border-2 border-dashed border-ink/20 rounded-lg py-6 cursor-pointer hover:border-terracotta/50 transition-colors"
            data-testid="file-upload-dropzone"
          >
            <UploadCloud size={22} className="text-ink-soft" />
            <span className="text-sm text-ink-soft">{file ? file.name : "Choose a file"}</span>
            <input
              type="file"
              className="hidden"
              accept={isInteractive ? ".docx" : undefined}
              onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
              data-testid="file-upload-input"
            />
          </label>
        )}

        {progress !== null && <Progress value={progress} data-testid="file-upload-progress" />}
        {error && (
          <p className="flex items-center gap-1.5 text-sm text-red-600" data-testid="file-upload-error">
            <FileWarning size={14} /> {error}
          </p>
        )}

        {importStage !== null && importStage !== "failed" && (
          <div className="space-y-2 py-2" data-testid="file-upload-import-progress">
            {IMPORT_STEPS.map((step, i) => {
              const currentIndex = IMPORT_STEPS.findIndex((s) => s.key === importStage);
              const state = i < currentIndex || importStage === "done" ? "done" : i === currentIndex ? "active" : "pending";
              return (
                <div key={step.key} className="flex items-center gap-2 text-sm" data-testid={`file-upload-import-step-${step.key}`} data-step-state={state}>
                  {state === "done" ? <Check size={14} className="text-terracotta" /> : state === "active" ? <Loader2 size={14} className="animate-spin text-terracotta" /> : <span className="h-3.5 w-3.5 rounded-full border border-ink/20" />}
                  <span className={state === "pending" ? "text-ink-soft" : "text-ink"}>{step.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {importStage === "failed" && importFailure && (
          <div className="space-y-3 rounded-md border border-red-200 bg-red-50 p-3" data-testid="file-upload-import-failed">
            <p className="flex items-center gap-1.5 text-sm text-red-700">
              <FileWarning size={14} /> {importFailure.message}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => { setImportStage(null); setImportFailure(null); setFile(null); }} data-testid="file-upload-import-try-another">
                Try another file
              </Button>
              <Button size="sm" variant="outline" onClick={handleUploadFailedAsStatic} data-testid="file-upload-import-use-static">
                Upload as Static file
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelFailedImport} data-testid="file-upload-import-cancel">
                Cancel
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="file-upload-cancel">Cancel</Button>
          {importStage === null && (
            <Button disabled={!canSubmit} onClick={handleUpload} data-testid="file-upload-confirm">
              {progress !== null ? `Uploading... ${progress}%` : "Upload"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
